import { get, reduce } from "lodash-es";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { usePermissionsContext } from "Bands/contexts";
import { copy } from "shared/assets";
import { APIErrorMessage, Button, Card, Loader } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import {
  IAPIError,
  ICardTheme,
  IPermission,
  IPermissionGroup,
  IPermissionType,
} from "shared/types";

import { SaveChangesModal } from "Bands/pages/permissions/containers/SaveChangesModal";
import { Prompt } from "react-router-dom";
import { useScrollToHashOnFirstRender } from "shared/utils";
import { Permission, PermissionGroup } from ".";
import { PermissionFieldGroup } from "./components";
import { StyledStickySave } from "./components/styles";
import { forceIsDirtyAtom } from "Bands/pages/permissions/state/permissions";
import { useAtomValue } from "jotai";

interface PermissionFormProps {
  onSubmit: (data: any) => void;
  permissions: IPermission[];
  permissionTypes: IPermissionType[];
  isSubmitting?: boolean;
  error?: string;
  apiError?: IAPIError | null;
  setWithRedirect: (withRedirect: boolean) => void;
  setNextLocation: (nextLocation: any) => void;
  nextLocation: any;
  hasbeenSubmitted: boolean;
  handleNextLocation: () => void;
}

export const PermissionForm: FC<PermissionFormProps> = ({
  onSubmit,
  permissions,
  isSubmitting,
  error,
  apiError,
  setWithRedirect,
  setNextLocation,
  hasbeenSubmitted,
  handleNextLocation,
}) => {
  const {
    rootGroups,
    childrenGroups,
    groupDependencies,
    disabledDependencies,
  } = usePermissionsContext();
  const { register, handleSubmit, setValue, control, formState, reset } =
    useForm();
  const watchAllFields = useWatch({ control });
  const [showModal, setShowModal] = useState(false);
  const forceIsDirty = useAtomValue(forceIsDirtyAtom);
  // if permissions change by patch call
  // update the values in the form
  useEffect(() => {
    const values = reduce(
      permissions,
      (result, permission) => {
        result = {
          ...result,
          [API_ENDPOINTS.PERMISSION_TYPE(permission.permission.id)]:
            permission.permission.locked || permission.granted,
        };

        return result;
      },
      {} as { [name: string]: boolean }
    );

    // set values of permissions
    Object.keys(values).forEach((path) => {
      setValue(path, values[path]);
    });
  }, [permissions, setValue]);

  useEffect(() => {
    if (hasbeenSubmitted) {
      reset(watchAllFields);
    }
    // eslint-disable-next-line
  }, [hasbeenSubmitted, reset]);

  const renderPermission = (
    {
      granted,
      permission: { id, label, description, type, dependencies, locked },
    }: IPermission,
    rootGroupId: string,
    isLastItem?: boolean
  ) => {
    const name = API_ENDPOINTS.PERMISSION_TYPE(id);

    return (
      <Permission
        name={name}
        label={label}
        description={description}
        permissionType={type}
        defaultValue={locked ? true : granted}
        locked={locked}
        register={register}
        value={get(watchAllFields, name)}
        dependencies={dependencies?.map(({ id }) => id)}
        disabledDependencies={disabledDependencies?.[name]}
        setValue={setValue}
        isLast={isLastItem}
        watchAllFields={watchAllFields}
        rootGroupId={rootGroupId}
      />
    );
  };

  const renderGroup = (
    group: IPermissionGroup,
    rootGroupId: string,
    isLastItem?: boolean
  ) => {
    const groupPermissions = permissions.filter(
      (perm) => perm.permission.permissionGroup?.id === group.id
    );

    const { id, label, description, type } = group;
    const firstPermission = groupDependencies?.[id]?.[0];

    if (!firstPermission) return null;

    return (
      <>
        <PermissionGroup
          name={API_ENDPOINTS.PERMISSION_GROUP(id)}
          label={label}
          description={description}
          permissionType={type}
          register={register}
          value={get(watchAllFields, API_ENDPOINTS.PERMISSION_GROUP(id))}
          firstPermission={firstPermission}
          disabledDependencies={disabledDependencies?.[firstPermission]}
          uncheckDependencies={groupDependencies?.[id]}
          setValue={setValue}
          watchAllFields={watchAllFields}
          isLast={isLastItem}
          rootGroupId={rootGroupId}
        >
          <PermissionFieldGroup isSubGroup>
            {groupPermissions.map((permission, permIndex) => (
              <React.Fragment key={permission.permission.id}>
                {renderPermission(
                  permission,
                  rootGroupId,
                  permIndex === groupPermissions.length - 1
                )}
              </React.Fragment>
            ))}
          </PermissionFieldGroup>
        </PermissionGroup>
      </>
    );
  };

  // permission without group
  // eg CREATE EVENT
  const renderTopPermission = (
    permission: IPermission,
    rootGroupId: string,
    isLastItem: boolean
  ) => {
    return <>{renderPermission(permission, rootGroupId, isLastItem)}</>;
  };

  const renderPermissions = () => {
    return (
      <>
        {rootGroups?.map(({ id: rootGroupId, label }, index) => (
          <Card
            id={"root-group-id-" + rootGroupId}
            title={label}
            theme={ICardTheme.secondary}
            key={rootGroupId}
          >
            {childrenGroups?.[index]?.map((child, childIndex) => {
              const isLastItem =
                childIndex === childrenGroups[index]?.length - 1;

              // permission without group
              if ("permissionGroup" in child) {
                const permissionType = child as IPermissionType;
                const permission = permissions.find(
                  (perm) => perm.permission.id === permissionType.id
                );

                if (!permission) return null;

                return (
                  <React.Fragment key={permission.permission.id}>
                    {renderTopPermission(permission, rootGroupId, isLastItem)}
                  </React.Fragment>
                );
              }

              // group with underlying permissions
              const group = child as IPermissionGroup;

              return (
                <React.Fragment key={group.id}>
                  {renderGroup(group, rootGroupId, isLastItem)}
                </React.Fragment>
              );
            })}
          </Card>
        ))}
      </>
    );
  };

  useEffect(() => {
    const handleBeforeUnload = (e: any) => {
      if (!showModal && (forceIsDirty || formState.isDirty)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [showModal, formState.isDirty, forceIsDirty]);

  useScrollToHashOnFirstRender();
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Prompt
        when={formState.isDirty || forceIsDirty}
        message={(nextLocation) => {
          setShowModal(true);
          setNextLocation(nextLocation);
          return false;
        }}
      />
      {showModal && (
        <SaveChangesModal
          handleSave={() => {
            setWithRedirect(true);
            handleSubmit(onSubmit)();
          }}
          isSubmitting={isSubmitting}
          handleClose={() => setShowModal(false)}
          error={error}
          apiError={apiError}
          handleNextLocation={handleNextLocation}
        />
      )}
      <Spacer size="l" style={{ position: "relative" }}>
        {renderPermissions()}
        <StyledStickySave style={{ position: "sticky" }}>
          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            <span>{copy.form.save}</span>
            {isSubmitting && <Loader size="s" />}
          </Button>
          {(error || apiError) && (
            <APIErrorMessage
              apiErrorMessage={error || copy.errors.general}
              apiError={apiError}
            />
          )}
        </StyledStickySave>
      </Spacer>
    </form>
  );
};
